import React from 'react';
import { Alert, Button, Collapse, Spin } from 'antd';
import {
  eventTitleOf, failCodeOffersAbandon, failDetailLineOf, failDetailOf, failedEventIdsOf, humanFailOf, partOrdinalOf, step1PartsOf, step1SegmentName,
  type FourViewModel, type WorkflowFourStatus,
} from './workflowFourUtils';

const PART_TEXT: Record<string, string> = { done: 'xong', running: 'đang chạy', stopped: 'đã dừng', failed: 'lỗi', pending: '–' };
const PART_ICON: Record<string, string> = { done: '✓', running: '', stopped: '❚❚', failed: '!', pending: '' };

interface Props {
  status: WorkflowFourStatus;
  view: FourViewModel;
  events: any[];
  /** Có việc ghi/yêu cầu đang bay (resume mất 10–27 s ở bản đo thật). */
  busyNote: string | null;
  needsActionShown: boolean;
  /** Sửa chữ được khi chuỗi không chạy (BE chặn ghi khi đang chạy). */
  canEditSource: boolean;
  onResume: () => void;
  onEditEvent: (event: any) => void;
  /** Mở hộp «Bỏ lượt chưa rõ (đối soát tay)». */
  onAbandon: () => void;
  /** Mở hộp «Bỏ plan này, tạo lại». */
  onDropPlan: () => void;
  /** Một thao tác ghi đang bay (khoá các nút tốn kém/không đảo ngược). */
  busy: boolean;
}

/** Màn tiến độ bước ① (chạy / dừng / lỗi một phần / lỗi) theo mockup `step1-*`: dòng trạng thái, dải 14 phần, chip video, lưới 14 phần, ngăn kỹ thuật. */
export const FourStep1Panel: React.FC<Props> = ({ status, view, events, busyNote, needsActionShown, canEditSource, onResume, onEditEvent, onAbandon, onDropPlan, busy }) => {
  const chain = status.chain;
  const parts = step1PartsOf(status);
  const ordinal = partOrdinalOf(status);
  const progress = status.step1Progress;
  const failedIds = failedEventIdsOf(status);
  const failView = humanFailOf(chain, status.terminal);
  const failDetail = failDetailOf(chain?.failDetail);
  const segmentName = step1SegmentName(chain?.segment ?? progress?.segment);
  const failed = view.screen === 'partial-fail' || view.screen === 'error';
  const detailLine = failDetailLineOf(chain);
  const blocked = !!view.primary.blocked;
  const uncertain = failCodeOffersAbandon(failView?.code);
  const eventLabel = (id: string) => { const index = events.findIndex((event) => event.id === id); return index >= 0 ? `Video ${events[index].eventOrder ?? index + 1}${eventTitleOf(status, events[index]) !== 'Không tên' ? ` — ${eventTitleOf(status, events[index])}` : ''}` : 'Một video'; };
  const doneVideos = progress?.total && progress.done ? `${progress.done}/${progress.total}` : null;

  return <section className="wf-sec wf4-progress" data-testid="four-progress">
    <div className="wf4-eyebrow">Bước 1</div>
    <h2>Kịch bản &amp; dàn dựng</h2>

    {view.screen === 'stopped' && <Alert className="wf4-banner" type="warning" showIcon message="Bạn đã dừng. Mọi phần đã xong được giữ lại." description={`Bấm «${view.primary.label}» để máy làm tiếp, không phải làm lại từ đầu.`} />}
    {failed && failView && !needsActionShown && <Alert className="wf4-banner" type="error" showIcon message={failView.title}
      description={<>{failView.hint}{detailLine && <p className="wf4-fail-detail" data-testid="fail-detail">Lỗi cụ thể: {detailLine.label ?? detailLine.message ?? 'chưa rõ'}{detailLine.label && detailLine.message ? ` — ${detailLine.message}` : ''}{detailLine.code ? ` (mã ${detailLine.code})` : ''}.</p>}</>}
      action={<span>
        {view.screen === 'error' && !failView.blocksResume && !blocked ? <Button size="small" disabled={!view.primary.enabled || !!busyNote} onClick={onResume}>{view.primary.label}</Button> : null}
        {uncertain ? <Button size="small" danger disabled={busy || !!busyNote} onClick={onAbandon}>Bỏ lượt chưa rõ (đối soát tay)</Button> : null}
      </span>} />}
    {view.primary.waiting && <Alert className="wf4-banner" type="info" showIcon data-testid="step1-waiting" message={view.primary.label} description={view.primary.why ?? 'Có việc khác đang chạy trên plan — xong là chạy tiếp được.'} />}
    {blocked && <Alert className="wf4-banner" type="warning" showIcon data-testid="step1-blocked" message={view.primary.label} description={view.primary.why ?? 'Máy không thể tự chạy tiếp từ chỗ này.'}
      action={<Button size="small" disabled={busy} onClick={onDropPlan}>Bỏ plan này, tạo lại</Button>} />}
    {failed && !failView && <Alert className="wf4-banner" type="error" showIcon message="Bước 1 dừng vì lỗi." description="Phần đã xong được giữ lại. Thử chạy tiếp; nếu vẫn lỗi, sửa chữ của video lỗi." />}
    {/* S12c fix3: chỉ khi chuỗi đang CHẠY repair ở đoạn ≤ 6 — `chain.repair` còn sót của plan cũ (smoke 3) không được hiện «đang dàn lại» sau khi xong */}
    {chain?.repair && chain.state === 'RUNNING' && chain.segment <= 6 && (chain.repair.eventIds?.length || chain.repair.eventId) ? <Alert className="wf4-banner" type="info" showIcon message="Sửa tuổi / đổi nhân vật đang được dàn lại"
      description={`Máy chỉ chạy lại ${(chain.repair.eventIds ?? [chain.repair.eventId || '']).map(eventLabel).join(', ')}. Khi xong, nhãn «đã dàn lại — đọc lại» sẽ xuất hiện.`} /> : null}
    {busyNote && <Alert className="wf4-banner" type="info" showIcon icon={<Spin size="small" />} message={busyNote} />}

    <div className={`wf4-now ${view.screen}`}>
      <span className="wf4-dot" aria-hidden />
      <div>
        <h3 className="wf4-now-title">
          {view.screen === 'running' ? <>Máy đang làm: {progress?.label ? progress.label.replace(/^Đang /, '') : segmentName}{progress?.total ? ` — ${progress.done} / ${progress.total}` : ''}</>
            : view.screen === 'stopped' ? <>Đã dừng ở phần «{segmentName}»{doneVideos ? ` — ${doneVideos}` : ''}</>
              : failedIds.length ? <>Phần «{segmentName}» — {failedIds.length} video lỗi</> : <>Không chạy được phần «{segmentName}»</>}
        </h3>
        <p className="muted">Phần {ordinal.current} / {ordinal.total}{view.screen === 'running' ? ' · bạn có thể rời trang, máy vẫn chạy' : ''}</p>
      </div>
    </div>

    <div className="wf4-strip" role="img" aria-label={`Phần ${ordinal.current} trên ${ordinal.total}`}>
      {parts.map((part) => <i key={part.key} className={`wf4-bar ${part.state}`} title={`${part.label}: ${PART_TEXT[part.state]}`} />)}
    </div>

    {events.length > 0 && <div className="wf4-chips" aria-label="Các video">
      {events.map((event, index) => {
        const failedChip = failedIds.includes(event.id);
        const clickable = canEditSource && failed;
        return <button key={event.id} type="button" className={`wf4-chip ${failedChip ? 'bad' : ''}`} disabled={!clickable}
          title={clickable ? `Sửa chữ ${eventLabel(event.id)}` : eventLabel(event.id)} onClick={() => onEditEvent(event)}>{event.eventOrder ?? index + 1}</button>;
      })}
    </div>}
    {failed && events.length > 0 && canEditSource && <p className="muted wf4-chip-hint">Bấm số video để sửa lời gốc của video đó, rồi «{view.primary.label}».</p>}

    {failedIds.length > 0 && <div className="wf4-fail-cards">
      {failedIds.map((id) => { const event = events.find((row) => row.id === id); return <div className="wf4-fail-card" key={id}>
        <div><b>{eventLabel(id)}</b><p>{failedIds.length === 1 && failDetail.message ? failDetail.message : 'Video này chưa qua phần vừa chạy.'}</p></div>
        <div className="wf4-fail-actions">
          {!blocked && <Button size="small" disabled={!view.primary.enabled || !!busyNote} onClick={onResume}>{view.primary.reconcile ? view.primary.label : 'Thử lại'}</Button>}
          {event && canEditSource && <Button size="small" onClick={() => onEditEvent(event)}>Sửa lời gốc</Button>}
        </div>
      </div>; })}
    </div>}

    <div className="wf4-parts">
      {parts.map((part) => <div className={`wf4-part ${part.state}`} key={part.key}>
        <span className="wf4-part-icon" aria-hidden>{part.state === 'running' ? <i className="wf4-spin" /> : PART_ICON[part.state]}</span>
        <div><b>{part.label}</b><small>{part.hint}</small>{part.note ? <small className={`wf4-part-note ${part.note.tone}`} data-testid={`part-note-${part.key}`}>{part.note.text}</small> : null}</div>
        <span className="wf4-part-state">{part.stateText ?? PART_TEXT[part.state]}</span>
      </div>)}
    </div>
    <p className="faint wf4-granular">Máy báo tiến độ theo từng phần lớn; số video đã xong của phần đang chạy nằm ở dòng «Máy đang làm».</p>

    <Collapse size="small" items={[{
      key: 'tech', label: 'Chi tiết kỹ thuật của lượt chạy này',
      children: <dl className="wf4-tech">
        <dt>Trạng thái chuỗi</dt><dd>{chain?.state ?? '—'} · đoạn {chain?.segment ?? '—'}/6{chain?.stopRequested ? ' · đã yêu cầu dừng' : ''}</dd>
        <dt>Đoạn đã xong</dt><dd>{chain?.done?.length ? chain.done.join(', ') : 'chưa có'}{chain?.staleDone?.length ? ` (có thể đã cũ: ${chain.staleDone.join(', ')})` : ''}</dd>
        {failView?.code && <><dt>Mã lỗi</dt><dd className="mono">{failView.code}</dd></>}
        {failDetail.code && failDetail.code !== failView?.code && <><dt>Mã lỗi chủ thể</dt><dd className="mono">{failDetail.code}</dd></>}
        {failDetail.message && <><dt>Chi tiết lỗi</dt><dd>{failDetail.message}</dd></>}
        <dt>Việc đang chạy ở server</dt><dd>{status.running ? 'có' : 'không'}</dd>
      </dl>,
    }]} />
  </section>;
};

export default FourStep1Panel;
